import { useState, useEffect } from 'react';
import { Upload, Link as LinkIcon, X, FolderOpen } from 'lucide-react';
import './BriefSheet.css';

export default function BriefSheet({ briefData, onApprove, onEdit, onAIEdit, isApproved = false, selectedBrand, creatives = [], outputType = 'image' }) {
  const [editingSection, setEditingSection] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [aiEditMode, setAiEditMode] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [productImageUrlInput, setProductImageUrlInput] = useState('');
  const [showLibraryModal, setShowLibraryModal] = useState(null); // 'logo' or 'productImage'

  // Initialize with brand logo if no logo is set
  useEffect(() => {
    if (selectedBrand?.logo && (!briefData.assets?.logos || briefData.assets.logos.length === 0)) {
      const brandLogo = {
        url: selectedBrand.logo,
        name: `${selectedBrand.name} Logo`,
        type: 'brand'
      };
      // Only update if we're in edit mode for assets
      if (editingSection === 'assets' && !editedFields.logos?.length) {
        setEditedFields(prev => ({
          ...prev,
          logos: [brandLogo]
        }));
      }
    }
  }, [selectedBrand, briefData.assets, editingSection, editedFields.logos]);

  const handleEditClick = (sectionKey) => {
    const section = briefData[sectionKey];

    if (sectionKey === 'imageDescription') {
      setEditedFields({ imageDescription: briefData.imageDescription || '' });
    } else if (sectionKey === 'videoDescription') {
      setEditedFields({ videoDescription: briefData.videoDescription || '' });
    } else if (sectionKey === 'videoSettings') {
      setEditedFields({
        videoLength: briefData.videoSettings?.videoLength || '',
        videoStyle: briefData.videoSettings?.videoStyle || '',
        videoType: briefData.videoSettings?.videoType || '',
        actorCount: briefData.videoSettings?.actorCount || '',
        actorDescription: briefData.videoSettings?.actorDescription || '',
        scriptStyle: briefData.videoSettings?.scriptStyle || ''
      });
    } else if (sectionKey === 'videoScript') {
      setEditedFields({ scriptContent: briefData.videoSettings?.scriptContent || '' });
    } else if (sectionKey === 'stillSettings') {
      setEditedFields({
        aspectRatio: briefData.stillSettings?.aspectRatio || '',
        compositionStyle: briefData.stillSettings?.compositionStyle || '',
        focalPoint: briefData.stillSettings?.focalPoint || '',
        variationCount: briefData.stillSettings?.variationCount || '',
        carouselCardCount: briefData.stillSettings?.carouselCardCount || '',
        hookStrategy: briefData.stillSettings?.hookStrategy || '',
        storyArcType: briefData.stillSettings?.storyArcType || '',
        visualConsistency: briefData.stillSettings?.visualConsistency || '',
        ctaPlacement: briefData.stillSettings?.ctaPlacement || ''
      });
    } else if (sectionKey === 'assets') {
      // Default to brand logo if no logos are set
      const defaultLogo = selectedBrand?.logo && (!briefData.assets?.logos || briefData.assets.logos.length === 0)
        ? [{
            url: selectedBrand.logo,
            name: `${selectedBrand.name} Logo`,
            type: 'brand'
          }]
        : briefData.assets?.logos || [];

      setEditedFields({
        logos: defaultLogo,
        productImages: briefData.assets?.productImages || []
      });
    } else if (section && typeof section === 'object') {
      setEditedFields({ ...section });
    }

    setEditingSection(sectionKey);
    setAiEditMode(false);
    setAiInstructions('');
  };

  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files);
    const key = type === 'logo' ? 'logos' : 'productImages';

    // For logos, only allow 1 file and replace existing
    if (type === 'logo') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setEditedFields(prev => ({
            ...prev,
            [key]: [{ url: event.target.result, name: file.name, type: 'upload' }]
          }));
        };
        reader.readAsDataURL(file);
      }
    } else {
      // For product images, allow multiple
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          setEditedFields(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), { url: event.target.result, name: file.name, type: 'upload' }]
          }));
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleUrlAdd = (type) => {
    const url = type === 'logo' ? logoUrlInput : productImageUrlInput;
    const key = type === 'logo' ? 'logos' : 'productImages';
    const setUrlInput = type === 'logo' ? setLogoUrlInput : setProductImageUrlInput;

    if (url.trim()) {
      if (type === 'logo') {
        // For logos, replace existing
        setEditedFields(prev => ({
          ...prev,
          [key]: [{ url: url.trim(), name: 'Logo', type: 'url' }]
        }));
      } else {
        // For product images, add to array
        setEditedFields(prev => ({
          ...prev,
          [key]: [...(prev[key] || []), { url: url.trim(), name: 'Product Image', type: 'url' }]
        }));
      }
      setUrlInput('');
    }
  };

  const handleRemoveImage = (type, index) => {
    const key = type === 'logo' ? 'logos' : 'productImages';
    setEditedFields(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSelectFromLibrary = (image, type) => {
    const key = type === 'logo' ? 'logos' : 'productImages';
    const newImage = {
      url: image.url || image.imageUrl,
      name: image.title || image.name || 'Library Image',
      type: 'library'
    };

    if (type === 'logo') {
      // Replace existing logo
      setEditedFields(prev => ({
        ...prev,
        [key]: [newImage]
      }));
    } else {
      // Add to product images
      setEditedFields(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), newImage]
      }));
    }
    setShowLibraryModal(null);
  };

  const handleFieldChange = (fieldKey, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSaveEdit = (sectionKey) => {
    onEdit(sectionKey, editedFields);
    setEditingSection(null);
    setEditedFields({});
    setAiEditMode(false);
    setAiInstructions('');
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedFields({});
    setAiEditMode(false);
    setAiInstructions('');
  };

  const handleToggleAIMode = () => {
    setAiEditMode(!aiEditMode);
    setAiInstructions('');
  };

  const handleAIEdit = async () => {
    if (!aiInstructions.trim() || !onAIEdit) return;

    setIsProcessingAI(true);
    try {
      // Convert edited fields to text for AI processing
      const currentText = Object.entries(editedFields)
        .map(([key, value]) => {
          const label = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
          return `${label}: ${value}`;
        })
        .join('\n\n');

      const updatedText = await onAIEdit(editingSection, currentText, aiInstructions);

      // Parse the AI response back into fields
      const updatedFields = {};
      const lines = updatedText.split('\n');
      lines.forEach(line => {
        const match = line.match(/^([^:]+):\s*(.+)$/);
        if (match) {
          const label = match[1].trim();
          const value = match[2].trim();
          // Convert label back to camelCase key
          const key = label.charAt(0).toLowerCase() + label.slice(1).replace(/\s+(\w)/g, (_, c) => c.toUpperCase());
          updatedFields[key] = value;
        }
      });

      setEditedFields(prev => ({ ...prev, ...updatedFields }));
      setAiInstructions('');
      setAiEditMode(false);
    } catch (error) {
      console.error('Error processing AI edit:', error);
      alert('Failed to process AI edit. Please try again.');
    } finally {
      setIsProcessingAI(false);
    }
  };

  const getPlaceholderExample = (key) => {
    const placeholders = {
      // Strategist fields
      product: 'e.g., Premium Ducted Heating Systems',
      targetAudience: 'e.g., Homeowners aged 35-55 in suburban Melbourne who value comfort and energy efficiency',
      objective: 'e.g., Drive enquiries and quote requests for winter heating installations',
      desiredEmotion: 'e.g., Warmth, security, and peace of mind knowing their family will stay comfortable',

      // Creative Director fields
      marketingAngle: 'e.g., Focus on reliability during the coldest nights when you need heating most',
      differentiators: 'e.g., 30+ years experience, Australian-made products, same-day service available',

      // Copywriter fields
      selectedHeadline: 'e.g., "Stay Warm All Winter Long" or "Australian Heating You Can Trust"',
      cta: 'e.g., Get a Free Quote, Book Your Installation, Call Now',
      tone: 'e.g., Professional and reassuring, with a warm friendly touch',

      // Art Director fields
      visualStyle: 'e.g., Warm and inviting with soft lighting, showing cozy family moments at home',
      imageType: 'e.g., Lifestyle photography showing real families relaxing in their warm homes',
      sceneType: 'e.g., People using/enjoying the product',
      peopleVibe: 'e.g., Multi-generational family looking comfortable and content, casual clothing',
      logo: 'e.g., Brand logo in top corner',
      // Designer phase removed - platform/layout are now selected after brief approval

      // Image description
      imageDescription: 'e.g., A warm living room scene with soft afternoon light streaming through windows. A family of four is relaxed on a comfortable couch, smiling and reading together. The room feels cozy and inviting with warm tones. In the background, subtly visible, is a modern heating vent. The overall mood is peaceful comfort and family togetherness.',

      // Video fields
      videoLength: 'e.g., 6s or 8s',
      videoStyle: 'e.g., Cinematic, Commercial, Documentary, UGC',
      videoType: 'e.g., Product Highlight, Actor-Driven, Mixed',
      actorCount: 'e.g., 1, 2, or 3-4',
      actorDescription: 'e.g., Young professional woman in her 30s, confident and approachable',
      scriptStyle: 'e.g., Narrative, Testimonial, Educational, Promotional',
      scriptContent: 'e.g., [0:00-0:02] Open on product hero shot...',
      videoDescription: 'e.g., A 6-second commercial video opening with a dramatic product reveal...'
    };

    return placeholders[key] || `Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}...`;
  };

  const renderImageUploadSection = (type) => {
    const key = type === 'logo' ? 'logos' : 'productImages';
    const images = editedFields[key] || [];
    const urlInput = type === 'logo' ? logoUrlInput : productImageUrlInput;
    const setUrlInput = type === 'logo' ? setLogoUrlInput : setProductImageUrlInput;
    const label = type === 'logo' ? 'Brand Logo' : 'Product Images';
    const isLogo = type === 'logo';
    const hasMaxImages = isLogo && images.length >= 1;

    return (
      <div className="image-upload-container">
        <h4>{label} {isLogo && <span style={{ fontSize: '0.875rem', fontWeight: 'normal', color: '#666' }}>(max 1)</span>}</h4>
        <div className="image-upload-section">
          <div className="upload-controls">
            <label className={`file-upload-btn ${hasMaxImages ? 'disabled' : ''}`}>
              <Upload size={16} />
              <span>{hasMaxImages ? 'Replace' : 'Upload'} {isLogo ? 'Logo' : 'Image'}</span>
              <input
                type="file"
                accept="image/*"
                multiple={!isLogo}
                onChange={(e) => handleFileUpload(e, type)}
                style={{ display: 'none' }}
                disabled={hasMaxImages && isLogo}
              />
            </label>

            <button
              type="button"
              onClick={() => setShowLibraryModal(type)}
              className="library-btn"
              disabled={hasMaxImages}
            >
              <FolderOpen size={16} />
              Select from Library
            </button>

            <div className="url-input-group">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="Or paste image URL"
                className="url-input"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlAdd(type))}
                disabled={hasMaxImages}
              />
              <button
                type="button"
                onClick={() => handleUrlAdd(type)}
                className="url-add-btn"
                disabled={!urlInput.trim() || hasMaxImages}
              >
                <LinkIcon size={16} />
                Add URL
              </button>
            </div>
          </div>

          {images.length > 0 && (
            <div className="image-preview-grid">
              {images.map((img, index) => (
                <div key={index} className="image-preview-item">
                  <img src={img.url} alt={img.name} />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(type, index)}
                    className="remove-image-btn"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                  {img.type === 'brand' && (
                    <span className="image-badge">Brand Logo</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditableFields = (fields) => {
    return (
      <div className="editable-fields">
        {Object.entries(fields).map(([key, value]) => {
          const label = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();

          const currentValue = editedFields[key] || '';
          const shouldShowPlaceholder = !currentValue || currentValue.trim() === '';
          const placeholder = shouldShowPlaceholder ? getPlaceholderExample(key) : `Enter ${label.toLowerCase()}...`;

          return (
            <div key={key} className="field-group">
              <label className="field-label">{label}:</label>
              {key === 'imageDescription' || (value && value.length > 100) ? (
                <textarea
                  value={currentValue}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="field-textarea"
                  rows={4}
                  placeholder={placeholder}
                />
              ) : (
                <input
                  type="text"
                  value={currentValue}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="field-input"
                  placeholder={placeholder}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSection = (title, sectionKey, content) => {
    const isEditing = editingSection === sectionKey;
    const section = briefData[sectionKey];

    return (
      <div className="brief-section" key={sectionKey}>
        <div className="section-header">
          <h3>{title}</h3>
          {!isEditing && !isApproved && (
            <button
              className="edit-section-btn"
              onClick={() => handleEditClick(sectionKey)}
              title="Edit this section"
            >
              ✏️ Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="edit-mode">
            {sectionKey === 'imageDescription' ? (
              renderEditableFields({ imageDescription: briefData.imageDescription })
            ) : sectionKey === 'videoDescription' ? (
              renderEditableFields({ videoDescription: briefData.videoDescription })
            ) : sectionKey === 'videoSettings' ? (
              renderEditableFields(editedFields)
            ) : sectionKey === 'stillSettings' ? (
              renderEditableFields(editedFields)
            ) : sectionKey === 'videoScript' ? (
              renderEditableFields({ scriptContent: briefData.videoSettings?.scriptContent })
            ) : sectionKey === 'assets' ? (
              <div>
                {renderImageUploadSection('logo')}
                {renderImageUploadSection('productImage')}
              </div>
            ) : (
              renderEditableFields(section)
            )}

            {aiEditMode && sectionKey !== 'assets' ? (
              <div className="ai-edit-section">
                <div className="ai-edit-header">
                  <span className="ai-icon">🤖</span>
                  <span className="ai-label">AI-Assisted Edit</span>
                </div>
                <textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  className="ai-instructions-textarea"
                  rows={3}
                  placeholder="Tell the AI what you'd like to change... (e.g., 'Make it more professional', 'Add emphasis on reliability', 'Shorten this')"
                  autoFocus
                  disabled={isProcessingAI}
                />
                <div className="ai-edit-actions">
                  <button
                    onClick={handleAIEdit}
                    className="ai-apply-btn"
                    disabled={!aiInstructions.trim() || isProcessingAI}
                  >
                    {isProcessingAI ? '✨ Processing...' : '✨ Apply AI Changes'}
                  </button>
                  <button
                    onClick={handleToggleAIMode}
                    className="ai-cancel-btn"
                    disabled={isProcessingAI}
                  >
                    Back to Manual Edit
                  </button>
                </div>
              </div>
            ) : (
              <div className="edit-actions">
                <button
                  onClick={handleToggleAIMode}
                  className="ai-mode-btn"
                  title="Let AI help you edit this section"
                >
                  🤖 AI Edit
                </button>
                <button onClick={() => handleSaveEdit(sectionKey)} className="save-btn">
                  ✓ Save
                </button>
                <button onClick={handleCancelEdit} className="cancel-btn">
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="section-content">
            {content}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="brief-sheet-container">
      <div className="brief-sheet">
        {/* Header */}
        <div className="brief-header">
          <div className="header-content">
            <h1>Creative Brief</h1>
            <div className="brief-meta">
              <span className="brief-date">{new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
              {briefData.brandName && (
                <span className="brief-brand">{briefData.brandName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Brief Sections */}
        <div className="brief-body">
          {briefData.strategist && renderSection(
            '🎯 Strategy & Foundation',
            'strategist',
            <>
              {briefData.strategist.product && (
                <div className="brief-item">
                  <strong>Product/Service:</strong>
                  <p>{briefData.strategist.product}</p>
                </div>
              )}
              {briefData.strategist.targetAudience && (
                <div className="brief-item">
                  <strong>Target Audience:</strong>
                  <p>{briefData.strategist.targetAudience}</p>
                </div>
              )}
              {briefData.strategist.objective && (
                <div className="brief-item">
                  <strong>Campaign Objective:</strong>
                  <p>{briefData.strategist.objective}</p>
                </div>
              )}
              {briefData.strategist.desiredEmotion && (
                <div className="brief-item">
                  <strong>Desired Emotion:</strong>
                  <p>{briefData.strategist.desiredEmotion}</p>
                </div>
              )}
              {briefData.strategist.geography && (
                <div className="brief-item">
                  <strong>Geography:</strong>
                  <p>{briefData.strategist.geography}</p>
                </div>
              )}
            </>
          )}

          {briefData.creativeDirector && renderSection(
            '💡 Creative Direction',
            'creativeDirector',
            <>
              {briefData.creativeDirector.marketingAngle && (
                <div className="brief-item">
                  <strong>Marketing Angle:</strong>
                  <p>{briefData.creativeDirector.marketingAngle}</p>
                </div>
              )}
              {briefData.creativeDirector.differentiators && (
                <div className="brief-item">
                  <strong>Key Differentiators:</strong>
                  <p>{briefData.creativeDirector.differentiators}</p>
                </div>
              )}
            </>
          )}

          {briefData.copywriter && renderSection(
            '✍️ Copy & Messaging',
            'copywriter',
            <>
              {briefData.copywriter.selectedHeadline && (
                <div className="brief-item">
                  <strong>Headline:</strong>
                  <p className="highlight">{briefData.copywriter.selectedHeadline}</p>
                </div>
              )}
              {briefData.copywriter.cta && (
                <div className="brief-item">
                  <strong>Call to Action:</strong>
                  <p>{briefData.copywriter.cta}</p>
                </div>
              )}
              {briefData.copywriter.tone && (
                <div className="brief-item">
                  <strong>Tone:</strong>
                  <p>{briefData.copywriter.tone}</p>
                </div>
              )}
              {briefData.copywriter.copyStyle && (
                <div className="brief-item">
                  <strong>Copy Style:</strong>
                  <p>{briefData.copywriter.copyStyle}</p>
                </div>
              )}
              {briefData.copywriter.legalRequirements && (
                <div className="brief-item">
                  <strong>Legal Requirements:</strong>
                  <p>{briefData.copywriter.legalRequirements}</p>
                </div>
              )}
            </>
          )}

          {briefData.artDirector && renderSection(
            '🎨 Visual Direction',
            'artDirector',
            <>
              {briefData.artDirector.visualStyle && (
                <div className="brief-item">
                  <strong>Visual Style:</strong>
                  <p>{briefData.artDirector.visualStyle}</p>
                </div>
              )}
              {briefData.artDirector.imageType && (
                <div className="brief-item">
                  <strong>Image Type:</strong>
                  <p>{briefData.artDirector.imageType}</p>
                </div>
              )}
              {briefData.artDirector.sceneType && (
                <div className="brief-item">
                  <strong>Scene Type:</strong>
                  <p>{briefData.artDirector.sceneType}</p>
                </div>
              )}
              {briefData.artDirector.photographyMood && (
                <div className="brief-item">
                  <strong>Lighting:</strong>
                  <p>{briefData.artDirector.photographyMood}</p>
                </div>
              )}
              {briefData.artDirector.peopleDescription && (
                <div className="brief-item">
                  <strong>People Description:</strong>
                  <p>{briefData.artDirector.peopleDescription}</p>
                </div>
              )}
              {briefData.artDirector.logo && (
                <div className="brief-item">
                  <strong>Logo:</strong>
                  <p>{briefData.artDirector.logo}</p>
                </div>
              )}
              {briefData.artDirector.brandColors && (
                <div className="brief-item">
                  <strong>Brand Colors:</strong>
                  <p>{Array.isArray(briefData.artDirector.brandColors) ? briefData.artDirector.brandColors.join(', ') : briefData.artDirector.brandColors}</p>
                </div>
              )}
              {briefData.artDirector.focusHierarchy && (
                <div className="brief-item">
                  <strong>Focus Hierarchy:</strong>
                  <p>{briefData.artDirector.focusHierarchy}</p>
                </div>
              )}
              {briefData.artDirector.textOverlays && (
                <div className="brief-item">
                  <strong>Text Overlays:</strong>
                  <p>{briefData.artDirector.textOverlays}</p>
                </div>
              )}
              {briefData.artDirector.textOverlayDetails && (
                <div className="brief-item">
                  <strong>Text Overlay Details:</strong>
                  <p>{briefData.artDirector.textOverlayDetails}</p>
                </div>
              )}
            </>
          )}

          {/* Designer phase removed - platform/layout are now selected after brief approval in Format Selection */}

          {renderSection(
            '📎 Brand Assets',
            'assets',
            <>
              {briefData.assets?.logos && briefData.assets.logos.length > 0 && (
                <div className="brief-item">
                  <strong>Logo:</strong>
                  <div className="asset-preview-grid">
                    {briefData.assets.logos.map((img, index) => (
                      <div key={index} className="asset-preview-item">
                        <img src={img.url} alt={img.name} />
                        <span className="asset-label">{img.name || 'Brand Logo'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {briefData.assets?.productImages && briefData.assets.productImages.length > 0 && (
                <div className="brief-item">
                  <strong>Product Images:</strong>
                  <div className="asset-preview-grid">
                    {briefData.assets.productImages.map((img, index) => (
                      <div key={index} className="asset-preview-item">
                        <img src={img.url} alt={img.name} />
                        <span className="asset-label">{img.name || 'Product Image'}</span>
                        {img.useCaseLabel && (
                          <span className="asset-use-case">{img.useCaseLabel}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(!briefData.assets?.logos || briefData.assets.logos.length === 0) &&
               (!briefData.assets?.productImages || briefData.assets.productImages.length === 0) && (
                <div className="brief-item">
                  <p style={{ color: '#999', fontStyle: 'italic' }}>No assets added yet. Click "Edit" to add logos or product images.</p>
                </div>
              )}
            </>
          )}

          {briefData.imageDescription && outputType !== 'video' && renderSection(
            '🖼️ Image Generation Plan',
            'imageDescription',
            <div className="brief-item">
              <p>{briefData.imageDescription}</p>
            </div>
          )}

          {/* Video Settings Section - only for video briefs */}
          {outputType === 'video' && briefData.videoSettings && renderSection(
            '🎬 Video Settings',
            'videoSettings',
            <>
              {briefData.videoSettings.videoLength && (
                <div className="brief-item">
                  <strong>Duration:</strong>
                  <p>{briefData.videoSettings.videoLength}</p>
                </div>
              )}
              {briefData.videoSettings.videoStyle && (
                <div className="brief-item">
                  <strong>Visual Style:</strong>
                  <p>{briefData.videoSettings.videoStyle}</p>
                </div>
              )}
              {briefData.videoSettings.videoType && (
                <div className="brief-item">
                  <strong>Video Type:</strong>
                  <p>{briefData.videoSettings.videoType === 'product_highlight' ? 'Product Highlight' :
                     briefData.videoSettings.videoType === 'actor_driven' ? 'Actor-Driven' :
                     briefData.videoSettings.videoType === 'mixed' ? 'Mixed (Product + Lifestyle)' :
                     briefData.videoSettings.videoType}</p>
                </div>
              )}
              {briefData.videoSettings.actorCount && (
                <div className="brief-item">
                  <strong>Cast:</strong>
                  <p>{briefData.videoSettings.actorCount} person(s)</p>
                </div>
              )}
              {briefData.videoSettings.actorDescription && (
                <div className="brief-item">
                  <strong>Actor Description:</strong>
                  <p>{briefData.videoSettings.actorDescription}</p>
                </div>
              )}
              {briefData.videoSettings.scriptStyle && (
                <div className="brief-item">
                  <strong>Script Style:</strong>
                  <p>{briefData.videoSettings.scriptStyle}</p>
                </div>
              )}
            </>
          )}

          {/* Still Settings Section removed from Brief Review - format selection happens after approval */}

          {/* Video Generation Plan - includes script and description */}
          {outputType === 'video' && (briefData.videoSettings?.scriptContent || briefData.videoDescription) && renderSection(
            '🎥 Video Generation Plan',
            'videoScript',
            <>
              {briefData.videoSettings?.scriptContent && (
                <div className="brief-item">
                  <strong>Director's Script:</strong>
                  <pre style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    margin: '8px 0 0 0',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    lineHeight: '1.6'
                  }}>
                    {briefData.videoSettings.scriptContent}
                  </pre>
                </div>
              )}
              {briefData.videoDescription && (
                <div className="brief-item" style={{ marginTop: briefData.videoSettings?.scriptContent ? '16px' : '0' }}>
                  <strong>Visual Description:</strong>
                  <p style={{ marginTop: '4px' }}>{briefData.videoDescription}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer with Approve Button */}
        <div className="brief-footer">
          <div className="approval-section">
            {isApproved ? (
              <div className="approved-status">
                <span className="approved-icon">✓</span>
                <p className="approved-text">Brief Approved - Proceeding with image generation</p>
              </div>
            ) : (
              <>
                <p className="approval-text">
                  Review the brief above. You can edit any section by clicking the "Edit" button, or use "AI Edit" to let AI make changes based on your instructions.
                  {outputType === 'video'
                    ? " When you're satisfied, click below to generate your video ad."
                    : " When you're satisfied, click below to proceed with image generation."}
                </p>
                <button
                  className="approve-btn"
                  onClick={onApprove}
                >
                  ✓ Approve and Continue
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Library Selection Modal */}
      {showLibraryModal && (
        <div className="library-modal-overlay" onClick={() => setShowLibraryModal(null)}>
          <div className="library-modal" onClick={(e) => e.stopPropagation()}>
            <div className="library-modal-header">
              <h3>Select from Library</h3>
              <button onClick={() => setShowLibraryModal(null)} className="modal-close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="library-modal-body">
              {creatives.length === 0 ? (
                <div className="library-empty">
                  <p>No images in your library yet.</p>
                  <p style={{ fontSize: '0.875rem', color: '#999' }}>Create some ads first to build your library.</p>
                </div>
              ) : (
                <div className="library-grid">
                  {creatives
                    .filter(creative => creative.imageUrl || creative.url)
                    .map((creative) => (
                      <div
                        key={creative.id}
                        className="library-item"
                        onClick={() => handleSelectFromLibrary(creative, showLibraryModal)}
                      >
                        <img src={creative.imageUrl || creative.url} alt={creative.title || 'Creative'} />
                        <div className="library-item-title">{creative.title || 'Untitled'}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
